import { FarmIcon } from '@assets/icons/FarmIcon';
import { SupportedEntitlements } from '@core/models/entitlements';
import { useAnomaliesByOrg } from '@hooks/queries/anomalyDetection/useAnomaliesByOrg';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useProperties } from '@hooks/queries/useProperties';
import { useEntitlements } from '@hooks/useEntitlements';
import { Divider, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { generatePath, matchPath, useLocation, useNavigate, useParams } from 'react-router';
import type { Property } from '../../../core/models/properties';
import { routePaths } from '../../../screens/routes';
import { PropertyListItem } from './PropertyListItem';

interface Props {
  close: () => void;
}

export const PropertyPopover = (props: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const entitlements = useEntitlements();
  const { properties } = useProperties();
  const { organization } = useOrganization();
  const hasAnomalyDetectionEnabled = entitlements.has(SupportedEntitlements.ANOMALY_DETECTION);
  const { data: anomaliesByOrg, isLoading } = useAnomaliesByOrg({ org: organization });

  // Leaving mid-prescription is guarded centrally by the route blocker; just navigate.
  const handleSelectProperty = (property: Property) => {
    const pathPattern = Object.values(routePaths).find((path) =>
      matchPath(path, location.pathname)
    );
    if (!pathPattern) return;

    navigate(generatePath(pathPattern, { ...params, propertyId: property.id }));
    props.close();
  };

  const handleGoToHall = () => {
    if (!organization) return;

    navigate(
      `${routePaths.organizations}/workspace/${organization.workspace_id}/organization/${organization.id}`,
      { state: { stopRedirect: true } }
    );
    props.close();
  };

  return (
    <div className="w-[320px] max-h-[411px] flex flex-col">
      <span className="px-2 py-1.5 text-xs text-sy-neutral-60 font-semibold">
        {organization?.name}
      </span>
      <div className="flex max-w-[320px] flex-col overflow-auto sy-scrollbar sy-scrollbar-light">
        {properties?.map((item) => {
          const anomalyData = anomaliesByOrg?.[item.id];
          return (
            <PropertyListItem
              key={item.id}
              property={item}
              onClick={handleSelectProperty}
              selected={item.id === params.propertyId}
              isLoadingAnomalyData={isLoading}
              anomalyData={
                hasAnomalyDetectionEnabled
                  ? {
                      quantity: anomalyData?.anomalies_quantity,
                      area: anomalyData?.anomalies_area,
                    }
                  : undefined
              }
            />
          );
        }) ?? <Spin />}
      </div>
      <Divider className="my-1" />
      <button
        type="button"
        className="flex items-center gap-2 px-2 py-1 text-start"
        onClick={handleGoToHall}
      >
        <div className="flex items-center p-1 rounded-lg bg-sy-green-10">
          <FarmIcon className="text-sy-teal-60" size={16} />
        </div>
        <span className="text-sy-teal-60">{t('header.properties.list')}</span>
      </button>
    </div>
  );
};
